import { dayjs, getAppTimezone } from '@lib/dayjs'

export type DateFormat =
  | 'short'
  | 'shortDate'
  | 'full'
  | 'dateAndTime'
  | 'fullMonth'
  | 'slashTime'
  | 'timeOnly'
  | 'dayMonthTime'
  | 'fullWithSeconds'
  | 'timeShort'
  | 'fullWithZone'

const dateFormats: Record<DateFormat, string> = {
  full: 'MMM DD, YYYY, h:mm A', // Jul 18, 2024, 9:28 PM
  short: 'MMM D, YYYY', // Jul 18, 2024
  shortDate: 'DD/MM/YYYY', // 18/07/2024
  dateAndTime: 'MMM D, h:mm A', // Jul 18, 9:28 PM
  fullMonth: 'MMMM DD, YYYY', // July 18, 2024
  slashTime: 'MM/DD, HH:mm:ss', // 07/18, 21:28:00
  timeOnly: 'h:mm:ss A', // 9:28 PM
  timeShort: 'hh:mm A', // 04:07 PM
  dayMonthTime: 'DD MMM HH:mm', // 18 Jul 21:28
  fullWithSeconds: 'MMM DD, YYYY, h:mm:ss A', // Jul 18, 2024, 9:28 PM
  fullWithZone: 'MMM DD, YYYY, h:mm:ss A z', // Jul 18, 2024, 9:28:00 PM GMT+4
}

export const formatDate = (date: string | null | undefined, format: DateFormat = 'full') => {
  if (!date) return null

  return dayjs.utc(date).tz(getAppTimezone()).format(dateFormats[format])
}

// '1990-07-18' => 35 (years), null when date is missing or in the future
export const getAge = (date: string | null | undefined) => {
  if (!date) return null

  const years = dayjs().diff(dayjs.utc(date), 'year')

  return years >= 0 ? years : null
}

// 204800 => 200.0 KB, 5242880 => 5.0 MB
const units = ['KB', 'MB', 'GB']
export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`

  let size = bytes / 1024
  let unit = 0

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }

  return `${size.toFixed(1)} ${units[unit]}`
}

// 1234.4324112 => 1,234.43
export const formatNumber = (amount: number | string) =>
  Number(amount).toLocaleString('en-us', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
